import { useMemo } from 'react'
import { useLastUsedLanguage } from 'hooks/queries/useLastUsedLanguage'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { DEFAULT_LANGUAGE } from 'components/Wizards/Wizard/Form/LanguageInputField/defaultContentLanguage'

const BRAND_KIT_CACHE_MS = 5 * 60 * 1000

/**
 * Default language for manual wizards and language inputs: the workspace brand kit's
 * content language when set, otherwise the workspace last-used language, then English.
 */
export const useDefaultContentLanguage = () => {
  const lastUsedQuery = useLastUsedLanguage()

  const { data: brandProfile, isLoading: isLoadingProfile } = useBrandProfile({
    cacheTime: BRAND_KIT_CACHE_MS,
  })
  const brandKitId = brandProfile?.brand_kit?.id

  const { data: brandKitData, isLoading: isLoadingBrandKit } = useBrandKit(brandKitId, {
    enabled: !!brandKitId,
    cacheTime: BRAND_KIT_CACHE_MS,
  })

  const brandKit = brandKitData?.brand_kit
  const brandKitLanguage = brandKit?.content_preferences?.language

  const language = useMemo(() => {
    if (brandKitLanguage) {
      return brandKitLanguage
    }
    return lastUsedQuery.data?.language || DEFAULT_LANGUAGE
  }, [brandKitLanguage, lastUsedQuery.data?.language])

  const isLoading = useMemo(() => {
    if (isLoadingProfile) return true
    if (brandKitId != null && isLoadingBrandKit) return true
    if (!brandKitLanguage && lastUsedQuery.isLoading) return true
    return false
  }, [isLoadingProfile, brandKitId, isLoadingBrandKit, brandKitLanguage, lastUsedQuery.isLoading])

  return {
    data: { language },
    isLoading,
  }
}
